import React, { useState, useEffect } from 'react';
import { Button, Typography, message } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { API_URL } from 'env';

const { Title, Text } = Typography;

interface ActiveAccountProps {
  setCurrentStep: (step: number) => void;
  profileData: any;
  navigate: any;
  setCompletedSteps: (steps: number[]) => void;
  completedSteps: number[];
}

const ActiveAccount: React.FC<ActiveAccountProps> = ({
  setCurrentStep,
  profileData,
  navigate,
  setCompletedSteps,
  completedSteps
}) => {
  const [countdown, setCountdown] = useState(3);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Auto complete this step
    if (!completedSteps.includes(3)) {
      setCompletedSteps([...completedSteps, 3]);
    }

    // Start countdown (skip if already loading)
    if (isLoading) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isLoading]);

  const handleComplete = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (!token) {
        message.error('Authentication token not found');
        localStorage.removeItem('registrationData');
        navigate('/accounts-overview');
        return;
      }

      if (!userData) {
        message.error('User data not found');
        localStorage.removeItem('registrationData');
        navigate('/accounts-overview');
        return;
      }

      const user = JSON.parse(userData);
      const userId = user?.id;

      if (!userId) {
        message.error('User ID not found');
        localStorage.removeItem('registrationData');
        navigate('/accounts-overview');
        return;
      }

      // Update user profile with collected data
      if (profileData && Object.keys(profileData).length > 0) {
        const updatePayload = {
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          birthday: profileData.birthday,
          is_active: true,
        };

        // Update user profile first
        const updateResponse = await fetch(`${API_URL}/api/v1/be/user/${userId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatePayload),
        });
        
        if (!updateResponse.ok) {
          if (updateResponse.status === 401) {
            message.error('Session expired');
            localStorage.removeItem('token');
            navigate('/sign-up');
            return;
          }
          throw new Error('Failed to update profile');
        }

        message.success('Profile updated successfully!');

        // Then fetch updated user data
        const userResponse = await fetch(`${API_URL}/api/v1/be/user/${userId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (userResponse.ok) {
          const responseData = await userResponse.json();
          if (responseData && responseData.data) {
            // Store updated user data in localStorage
            localStorage.setItem('user', JSON.stringify(responseData.data));
            localStorage.removeItem('registrationData');
            navigate('/accounts-overview');
          } else {
            localStorage.removeItem('registrationData');
            navigate('/accounts-overview');
          }
        } else if (userResponse.status === 401) {
          message.error('Session expired');
          localStorage.removeItem('token');
          navigate('/sign-up');
        } else {
          localStorage.removeItem('registrationData');
          navigate('/accounts-overview');
        }
      } else {
        // No profile data, just navigate
        localStorage.removeItem('registrationData');
        navigate('/accounts-overview');
      }
    } catch (error) {
      console.error('Error completing profile setup:', error);
      message.error('An error occurred during profile setup');
      // Still navigate on error
      localStorage.removeItem('registrationData');
      navigate('/accounts-overview');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="wallet-setup-step flex flex-col items-center justify-center min-h-[400px] text-center">
      {/* Success Icon */}
      <div className="mb-8">
        <CheckCircleOutlined className="text-8xl !text-green-500" />
      </div>

      {/* Success Message */}
      <div className="mb-4">
        <Title level={2} className="!text-white !mb-2 !font-bold">
          Last step - Account Activated!
        </Title>
        <Text className="!text-gray-300 text-lg block leading-relaxed">
          Your account has been created successfully.
        </Text>
      </div>

      {/* Action Button */}
      <div className="w-full max-w-md">
        <Button
          type="primary"
          size="large"
          onClick={handleComplete}
          loading={isLoading}
          disabled={isLoading}
          className="w-full h-14 bg-green-500 hover:bg-green-600 border-green-500 hover:border-green-600 text-white font-semibold text-lg rounded-lg"
        >
          GO TO YOUR ACCOUNT
        </Button>
        
        {countdown > 0 && !isLoading && (
          <Text className="!text-gray-400 text-sm mt-3 block">
            Redirecting in {countdown} seconds...
          </Text>
        )}
      </div>
    </div>
  );
};

export default ActiveAccount;
